import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import { Badge, Button, Input } from '../atoms';
import { useToast } from './Toast';
import { ProviderPickerModal } from './ProviderPickerModal';
import { AddContactModal, type ContactHostEntity } from './EntityContactsPanel';
import { activeMention } from './EntityNotesPanel';
import { sanitizeHtml } from '../lib/sanitizeHtml';
import { useEntityPermission } from '../lib/usePermissions';
import { isConnected, mailErrorMessage, useMailConnection, useSignatures } from '../lib/mail';
import { CONTACT_HOSTS, DOCUMENT_FK, feedKey, isEmailAddress, htmlToText, textToHtml, type FeedNote } from '../lib/recordFeed';

/**
 * ActivityComposer — the one composer at the foot of a record's feed (spec 0014, AC-9 / AC-11),
 * after ClickUp's task activity panel: a Comment / Email toggle.
 *
 * COMMENT: today's note composer. @ opens the teammate picker; a reply carries parent_note_id.
 *   POST /api/workflow/add-note
 * EMAIL: From (the user's Outlook link; opens the provider picker), To and Cc (the record's
 *   contacts plus free entry, with a save-as-contact offer after pushing), Subject, body,
 *   attachments from the record's documents, the signature chip, the firm's compliance note
 *   read-only, and Push to Outlook Drafts. appealstack never sends the email: it lands in the
 *   user's own Outlook Drafts and they send it from Outlook.
 *   The draft is saved on the record as the user writes (on blur), so it survives the
 *   Microsoft sign-in round trip: POST /api/mail/drafts {push:false}, then PUT /api/mail/drafts/:id.
 *   Push: POST /api/mail/drafts/:id/push.
 */

interface MentionCandidate { id: string; name: string; role_label: string | null; group: string }
interface ContactRow { id: string; first_name: string | null; last_name: string | null; email: string | null; role: string | null }
interface DocumentRow { id: string; name: string | null; filename?: string | null; document_type?: string | null }
interface SavedDraft {
  id: string; status: string; to: string[]; cc: string[] | null; subject: string; body_html?: string;
  signature_id: string | null; attachment_document_ids: string[]; draft_key: string | null;
}
interface DraftResponse { draft: SavedDraft; linked: Array<{ document_id: string; filename: string; url: string }> }

export interface ActivityComposerProps {
  entityType: string;
  entityId: string;
  replyTo?: FeedNote | null;
  onCancelReply?: () => void;
  /** A saved draft to resume (from the feed's "Resume draft"). */
  resumeDraftId?: string | null;
  onResumed?: () => void;
}

const storageKey = (t: string, id: string) => `appealstack.composer.${t}.${id}`;
const newDraftKey = () => `c-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

function errText(err: unknown): { message: string; code?: string } {
  if (err instanceof ApiClientError && err.body && typeof err.body === 'object') {
    const b = err.body as { error?: unknown; code?: unknown };
    return { message: String(b.error ?? err.message), code: typeof b.code === 'string' ? b.code : undefined };
  }
  return { message: mailErrorMessage(err) };
}

export function ActivityComposer({ entityType, entityId, replyTo = null, onCancelReply, resumeDraftId = null, onResumed }: ActivityComposerProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const canComment = useEntityPermission('note', 'can_add');
  const [mode, setMode] = useState<'comment' | 'email'>('comment');

  // ── Comment ─────────────────────────────────────────────────────────────
  const [comment, setComment] = useState('');
  const [mentions, setMentions] = useState<{ id: string; name: string }[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionHi, setMentionHi] = useState(0);
  const [posting, setPosting] = useState(false);
  const taRef = useRef<HTMLTextAreaElement>(null);
  const candidatesQuery = useQuery<{ candidates: MentionCandidate[] }>({
    queryKey: ['mention-candidates', entityType, entityId],
    queryFn: ({ signal }) => api.get(`/api/mention-candidates/${entityType}/${entityId}`, signal),
    staleTime: 5 * 60_000, enabled: mode === 'comment',
  });
  const candidates = useMemo(() => {
    if (mentionQuery === null) return [];
    const q = mentionQuery.toLowerCase();
    return (candidatesQuery.data?.candidates ?? []).filter((c) => c.name.toLowerCase().includes(q)).slice(0, 8);
  }, [mentionQuery, candidatesQuery.data]);

  useEffect(() => { if (replyTo) { setMode('comment'); taRef.current?.focus(); } }, [replyTo]);

  function onCommentChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setComment(e.target.value);
    const active = activeMention(e.target.value, e.target.selectionStart ?? e.target.value.length);
    setMentionQuery(active ? active.query : null);
    setMentionHi(0);
  }
  function pickMention(c: MentionCandidate) {
    const caret = taRef.current?.selectionStart ?? comment.length;
    const active = activeMention(comment, caret);
    if (!active) return;
    const before = comment.slice(0, active.at);
    const insert = `@${c.name} `;
    setComment(before + insert + comment.slice(caret));
    setMentions((prev) => (prev.some((m) => m.id === c.id) ? prev : [...prev, { id: c.id, name: c.name }]));
    setMentionQuery(null);
  }
  function onCommentKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (mentionQuery === null || candidates.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setMentionHi((i) => (i + 1) % candidates.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setMentionHi((i) => (i - 1 + candidates.length) % candidates.length); }
    else if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickMention(candidates[Math.min(mentionHi, candidates.length - 1)]); }
    else if (e.key === 'Escape') { e.preventDefault(); setMentionQuery(null); }
  }
  async function postComment() {
    const content = comment.trim();
    if (!content) return;
    const ids = Array.from(new Set(mentions.filter((m) => comment.includes(`@${m.name}`)).map((m) => m.id)));
    const body: Record<string, unknown> = { entity_type: entityType, entity_id: entityId, content, mentioned_user_ids: ids };
    if (replyTo) body.parent_note_id = replyTo.id;
    setPosting(true);
    try {
      await api.post('/api/workflow/add-note', body);
      setComment(''); setMentions([]); setMentionQuery(null);
      if (onCancelReply) onCancelReply();
      await queryClient.invalidateQueries({ queryKey: feedKey(entityType, entityId) });
      queryClient.invalidateQueries({ queryKey: ['entity-notes', entityType, entityId] });
    } catch (err) {
      toast({ variant: 'error', message: errText(err).message });
    } finally { setPosting(false); }
  }

  // ── Email ───────────────────────────────────────────────────────────────
  const conn = useMailConnection(mode === 'email');
  const connected = isConnected(conn.data);
  const signaturesQuery = useSignatures(mode === 'email');
  const signatures = signaturesQuery.data ?? [];
  const noteQuery = useQuery<{ email_compliance_note: { enabled: boolean; html: string } }>({
    queryKey: ['org-settings', 'email-compliance-note'],
    queryFn: ({ signal }) => api.get('/api/org-settings/email-compliance-note', signal), retry: false, enabled: mode === 'email',
  });
  const complianceNote = noteQuery.data?.email_compliance_note;
  const contactsQuery = useQuery<ContactRow[]>({
    queryKey: ['contacts-by-entity', entityType, entityId, 'composer'],
    queryFn: ({ signal }) => api.get(`/api/contacts-by-entity/${entityType}/${entityId}`, signal), retry: false, enabled: mode === 'email',
  });
  const recordContacts = (Array.isArray(contactsQuery.data) ? contactsQuery.data : []).filter((c) => c.email);
  const docFk = DOCUMENT_FK[entityType];
  const docsQuery = useQuery<DocumentRow[]>({
    queryKey: ['documents', docFk, entityId],
    queryFn: ({ signal }) => api.get(`/api/documents?${docFk}=${entityId}`, signal), retry: false, enabled: mode === 'email' && !!docFk,
  });
  const docs = Array.isArray(docsQuery.data) ? docsQuery.data : [];

  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [toInput, setToInput] = useState('');
  const [ccInput, setCcInput] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [signatureId, setSignatureId] = useState<string | null | undefined>(undefined);
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  const [showAttach, setShowAttach] = useState(false);
  const [showSignatures, setShowSignatures] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(null);
  const [draftKey, setDraftKey] = useState<string>(() => newDraftKey());
  const [pushing, setPushing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [savePrompt, setSavePrompt] = useState<string[]>([]);
  const [addContactFor, setAddContactFor] = useState<string | null>(null);

  const defaultSignature = signatures.find((s) => s.is_default) ?? null;
  const chosenSignature = signatureId === undefined ? defaultSignature : signatures.find((s) => s.id === signatureId) ?? null;

  function payload(extra: Record<string, unknown> = {}) {
    return {
      related_entity_type: entityType, related_entity_id: entityId, draft_key: draftKey,
      to, cc, subject, body_html: textToHtml(body),
      signature_id: signatureId === undefined ? (defaultSignature?.id ?? null) : signatureId,
      attachment_document_ids: attachmentIds, ...extra,
    };
  }
  const hasContent = () => !!(subject.trim() || body.trim() || to.length || cc.length);

  function remember(id: string, key: string) {
    try { window.sessionStorage.setItem(storageKey(entityType, entityId), JSON.stringify({ id, key })); } catch { /* storage unavailable */ }
  }
  function forget() {
    try { window.sessionStorage.removeItem(storageKey(entityType, entityId)); } catch { /* storage unavailable */ }
  }

  /** Save the draft on the record (no push). Returns the draft id. */
  async function saveDraft(): Promise<string | null> {
    if (!hasContent()) return draftId;
    try {
      const res = draftId
        ? await api.put<DraftResponse>(`/api/mail/drafts/${draftId}`, payload())
        : await api.post<DraftResponse>('/api/mail/drafts', payload({ push: false }));
      setDraftId(res.draft.id);
      remember(res.draft.id, draftKey);
      return res.draft.id;
    } catch (err) {
      toast({ variant: 'error', message: `Draft not saved: ${errText(err).message}` });
      return draftId;
    }
  }

  function load(d: SavedDraft) {
    setMode('email');
    setDraftId(d.id);
    if (d.draft_key) setDraftKey(d.draft_key);
    setTo(d.to || []); setCc(d.cc || []); setSubject(d.subject || ''); setBody(htmlToText(d.body_html || ''));
    setSignatureId(d.signature_id ?? undefined); setAttachmentIds(d.attachment_document_ids || []);
  }

  // Resume a draft saved on this record (after the Microsoft round trip, or from the feed).
  useEffect(() => {
    let id = resumeDraftId;
    if (!id) {
      try { const raw = window.sessionStorage.getItem(storageKey(entityType, entityId)); id = raw ? JSON.parse(raw).id : null; } catch { id = null; }
    }
    if (!id) return;
    let cancelled = false;
    api.get<{ draft: SavedDraft }>(`/api/mail/drafts/${id}`)
      .then((r) => { if (!cancelled && r.draft.status !== 'pushed') load(r.draft); if (onResumed) onResumed(); })
      .catch(() => { forget(); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityType, entityId, resumeDraftId]);

  function addRecipient(list: string[], set: (v: string[]) => void, raw: string, clear: () => void) {
    const e = raw.trim().replace(/[,;]$/, '');
    if (!e) return;
    if (!isEmailAddress(e)) { toast({ variant: 'error', message: `${e} is not an email address.` }); return; }
    if (!list.some((x) => x.toLowerCase() === e.toLowerCase())) set([...list, e]);
    clear();
  }

  function reset() {
    setTo([]); setCc([]); setSubject(''); setBody(''); setAttachmentIds([]); setSignatureId(undefined);
    setDraftId(null); setDraftKey(newDraftKey()); forget();
  }

  async function push() {
    setPushing(true);
    try {
      const res = draftId
        ? await (async () => { await api.put(`/api/mail/drafts/${draftId}`, payload()); return api.post<DraftResponse>(`/api/mail/drafts/${draftId}/push`, {}); })()
        : await api.post<DraftResponse>('/api/mail/drafts', payload());
      const known = new Set(recordContacts.map((c) => (c.email as string).toLowerCase()));
      const unknown = [...to, ...cc].filter((e) => !known.has(e.toLowerCase()));
      toast({ variant: 'success', message: res.linked?.length
        ? `In your Outlook Drafts. ${res.linked.length} large file${res.linked.length === 1 ? ' was' : 's were'} added as a link instead of an attachment.`
        : 'In your Outlook Drafts. Review it and send it from Outlook.' });
      reset();
      if (CONTACT_HOSTS.has(entityType) && unknown.length) setSavePrompt(unknown);
      await queryClient.invalidateQueries({ queryKey: feedKey(entityType, entityId) });
    } catch (err) {
      const { message, code } = errText(err);
      if (err instanceof ApiClientError && err.body && typeof err.body === 'object' && 'draft' in err.body) {
        const d = (err.body as { draft: SavedDraft }).draft;
        setDraftId(d.id); remember(d.id, draftKey);
      }
      if (code === 'no_connection' || code === 'reconnect') setPickerOpen(true);
      toast({ variant: 'error', message });
      await queryClient.invalidateQueries({ queryKey: feedKey(entityType, entityId) });
    } finally { setPushing(false); }
  }

  const returnTo = typeof window !== 'undefined' ? `${window.location.pathname}${window.location.search}` : undefined;
  const canPush = connected && to.length > 0 && !!subject.trim() && !pushing;
  const pushBlockedReason = !conn.data?.configured ? 'Outlook is not set up for your firm yet.'
    : !connected ? 'Connect Outlook to push drafts.'
      : !to.length ? 'Add a recipient.'
        : !subject.trim() ? 'Add a subject.' : '';

  // ── Render ──────────────────────────────────────────────────────────────
  const chip = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 8px', borderRadius: 999, border: '1px solid var(--color-border)', background: 'var(--color-bg)', fontSize: 'var(--fs-xs)', cursor: 'pointer' } as const;
  const label = { fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', minWidth: 44 } as const;
  const hint = { margin: 0, fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)' } as const;
  const toggleBtn = (active: boolean) => ({ border: 'none', background: active ? 'var(--brand-primary-bg)' : 'transparent', color: active ? 'var(--brand-primary)' : 'var(--color-ink-2)', fontWeight: 'var(--fw-medium)', padding: '4px 12px', borderRadius: 'var(--r-md)', cursor: 'pointer', fontSize: 'var(--fs-sm)' }) as const;

  const recipientRow = (name: 'To' | 'Cc', list: string[], set: (v: string[]) => void, input: string, setInput: (v: string) => void) => {
    const q = input.trim().toLowerCase();
    const suggestions = q ? recordContacts.filter((c) => !list.includes(c.email as string) && `${c.first_name ?? ''} ${c.last_name ?? ''} ${c.email}`.toLowerCase().includes(q)).slice(0, 6) : [];
    return (
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
        <span style={{ ...label, paddingTop: 6 }}>{name}</span>
        <div style={{ flex: 1, minWidth: 200, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', position: 'relative' }}>
          {list.map((e) => (
            <span key={e} style={{ ...chip, cursor: 'default' }}>{e}
              <button type="button" aria-label={`Remove ${e} from ${name}`} onClick={() => set(list.filter((x) => x !== e))} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 0 }}>×</button>
            </span>
          ))}
          <input
            aria-label={`${name} recipients`}
            value={input}
            placeholder={list.length ? '' : 'Name or email'}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',' || e.key === ';') { e.preventDefault(); addRecipient(list, set, input, () => setInput('')); } }}
            onBlur={() => { if (isEmailAddress(input.trim())) addRecipient(list, set, input, () => setInput('')); saveDraft(); }}
            style={{ flex: 1, minWidth: 140, border: 'none', outline: 'none', background: 'transparent', fontSize: 'var(--fs-sm)', padding: '4px 0' }}
          />
          {suggestions.length > 0 && (
            <ul role="listbox" aria-label={`${name} suggestions`} style={menuStyle}>
              {suggestions.map((c) => (
                <li key={c.id} role="option" aria-selected={false} onMouseDown={(ev) => { ev.preventDefault(); set([...list, c.email as string]); setInput(''); }} style={menuItemStyle}>
                  {[c.first_name, c.last_name].filter(Boolean).join(' ') || c.email} <span style={hint}>{c.email}</span>
                  {c.role && <Badge variant="neutral">{c.role.replace(/_/g, ' ')}</Badge>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  };

  return (
    <div data-testid="activity-composer" id="composer" style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--r-lg)', background: 'var(--color-card)', padding: 'var(--sp-3)', display: 'grid', gap: 'var(--sp-2)' }}>
      <div role="group" aria-label="Compose" style={{ display: 'flex', gap: 'var(--sp-1)' }}>
        <button type="button" aria-pressed={mode === 'comment'} onClick={() => setMode('comment')} style={toggleBtn(mode === 'comment')}>Comment</button>
        <button type="button" aria-pressed={mode === 'email'} onClick={() => setMode('email')} style={toggleBtn(mode === 'email')}>Email</button>
      </div>

      {mode === 'comment' && (canComment ? (
        <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
          {replyTo && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)', fontSize: 'var(--fs-xs)' }}>
              <span>Replying to {replyTo.author_name ?? 'a comment'}</span>
              <button type="button" onClick={onCancelReply} style={linkBtn}>Cancel reply</button>
            </div>
          )}
          <div style={{ position: 'relative' }}>
            <textarea ref={taRef} aria-label={replyTo ? 'Reply' : 'New comment'} rows={3} value={comment} onChange={onCommentChange} onKeyDown={onCommentKeyDown}
              placeholder="Write a comment… use @ to mention a teammate" style={areaStyle} />
            {mentionQuery !== null && candidates.length > 0 && (
              <ul role="listbox" aria-label="Mention a teammate" style={menuStyle}>
                {candidates.map((c, i) => (
                  <li key={c.id} role="option" aria-selected={i === mentionHi} onMouseDown={(e) => { e.preventDefault(); pickMention(c); }}
                    style={{ ...menuItemStyle, background: i === mentionHi ? 'var(--brand-primary-bg)' : 'transparent' }}>
                    {c.name} {c.role_label && <Badge variant="info">{c.role_label}</Badge>}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-2)' }}>
            <p style={hint}>Internal only. Comments never appear in the client portal.</p>
            <Button variant="primary" size="sm" onClick={postComment} loading={posting} disabled={!comment.trim() || posting}>{replyTo ? 'Post reply' : 'Post comment'}</Button>
          </div>
        </div>
      ) : <p style={hint}>Your role cannot add comments here.</p>)}

      {mode === 'email' && (
        <div style={{ display: 'grid', gap: 'var(--sp-2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <span style={label}>From</span>
            <button type="button" data-testid="from-chip" onClick={() => setPickerOpen(true)} style={chip}>
              {connected ? conn.data!.connection!.account_email
                : conn.data?.connection?.status === 'error' ? 'Reconnect Outlook'
                  : conn.data && !conn.data.configured ? 'Outlook not available' : 'Connect Outlook'}
            </button>
          </div>
          {recipientRow('To', to, setTo, toInput, setToInput)}
          {recipientRow('Cc', cc, setCc, ccInput, setCcInput)}
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <span style={label}>Subject</span>
            <Input aria-label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} onBlur={() => saveDraft()} style={{ flex: 1 }} />
          </div>
          <textarea aria-label="Email body" rows={6} value={body} onChange={(e) => setBody(e.target.value)} onBlur={() => saveDraft()} placeholder="Write your email…" style={areaStyle} />

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)', alignItems: 'center', position: 'relative' }}>
            <button type="button" data-testid="signature-chip" aria-haspopup="menu" aria-expanded={showSignatures} onClick={() => setShowSignatures(!showSignatures)} style={chip}>
              Signature: {chosenSignature ? chosenSignature.name : 'none'}
            </button>
            {showSignatures && (
              <ul role="menu" aria-label="Signatures" style={{ ...menuStyle, top: '100%', left: 0, right: 'auto', minWidth: 220 }}>
                {signatures.map((s) => (
                  <li key={s.id} role="menuitemradio" aria-checked={chosenSignature?.id === s.id} tabIndex={0} style={menuItemStyle}
                    onClick={() => { setSignatureId(s.id); setShowSignatures(false); }} onKeyDown={(e) => { if (e.key === 'Enter') { setSignatureId(s.id); setShowSignatures(false); } }}>
                    {s.name}{s.is_default ? ' (default)' : ''}
                  </li>
                ))}
                <li role="menuitemradio" aria-checked={!chosenSignature} tabIndex={0} style={menuItemStyle} onClick={() => { setSignatureId(null); setShowSignatures(false); }}>No signature</li>
                <li role="menuitem" style={menuItemStyle}><a href="/account/email">{signatures.length ? 'Manage signatures' : 'Add a signature'}</a></li>
              </ul>
            )}
            {docFk && (
              <button type="button" aria-expanded={showAttach} onClick={() => setShowAttach(!showAttach)} style={chip}>
                Attach{attachmentIds.length ? ` (${attachmentIds.length})` : ''}
              </button>
            )}
          </div>
          {showAttach && (
            <div role="group" aria-label="Attach documents from this record" style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', padding: 'var(--sp-2)', maxHeight: 160, overflowY: 'auto', display: 'grid', gap: 4 }}>
              {docs.length === 0 && <span style={hint}>No documents on this record.</span>}
              {docs.map((d) => (
                <label key={d.id} style={{ display: 'flex', gap: 'var(--sp-2)', fontSize: 'var(--fs-sm)' }}>
                  <input type="checkbox" checked={attachmentIds.includes(d.id)} onChange={() => setAttachmentIds(attachmentIds.includes(d.id) ? attachmentIds.filter((x) => x !== d.id) : [...attachmentIds, d.id])} />
                  {d.name || d.filename} {d.document_type && <span style={hint}>{d.document_type}</span>}
                </label>
              ))}
              <span style={hint}>Files over 25 MB are added as a link.</span>
            </div>
          )}
          {chosenSignature && (
            <div data-testid="signature-preview" style={{ fontSize: 'var(--fs-sm)', color: 'var(--color-ink-2)', borderLeft: '3px solid var(--color-border)', paddingLeft: 'var(--sp-2)' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(chosenSignature.body_html) }} />
          )}
          {complianceNote?.enabled && complianceNote.html && (
            <div data-testid="compliance-note" aria-label="Compliance note (added to every email)" style={{ fontSize: 'var(--fs-xs)', color: 'var(--color-ink-3)', borderTop: '1px solid var(--color-border)', paddingTop: 'var(--sp-2)' }}>
              <span style={{ display: 'block', marginBottom: 2 }}>Added by your firm to every email:</span>
              <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(complianceNote.html) }} />
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
            <p id="push-hint" style={hint}>{pushBlockedReason || 'Goes to your Outlook Drafts. You send it from Outlook.'}{draftId ? ' Draft saved on this record.' : ''}</p>
            <span title={pushBlockedReason || undefined}>
              <Button variant="primary" size="sm" onClick={push} loading={pushing} disabled={!canPush} aria-describedby="push-hint">Push to Outlook Drafts</Button>
            </span>
          </div>
        </div>
      )}

      {savePrompt.length > 0 && (
        <div role="status" style={{ fontSize: 'var(--fs-sm)', display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-2)', alignItems: 'center' }}>
          <span>Not a contact on this record yet:</span>
          {savePrompt.map((e) => <Button key={e} variant="secondary" size="sm" onClick={() => setAddContactFor(e)}>Save {e} as a contact</Button>)}
          <button type="button" onClick={() => setSavePrompt([])} style={linkBtn}>Dismiss</button>
        </div>
      )}

      <ProviderPickerModal open={pickerOpen} onClose={() => setPickerOpen(false)} returnTo={returnTo} beforeConnect={async () => { await saveDraft(); }} />
      {addContactFor && CONTACT_HOSTS.has(entityType) && (
        <AddContactModal
          open
          onClose={() => { setSavePrompt(savePrompt.filter((e) => e !== addContactFor)); setAddContactFor(null); }}
          entityType={entityType as ContactHostEntity}
          entityId={entityId}
          invalidateKey={['contacts-by-entity', entityType, entityId]}
          initialEmail={addContactFor}
        />
      )}
    </div>
  );
}

const areaStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box', padding: 'var(--sp-2) var(--sp-3)', fontSize: 'var(--fs-sm)', borderRadius: 'var(--r-md)',
  border: '1px solid var(--color-border)', resize: 'vertical', fontFamily: 'inherit', background: 'var(--color-input, var(--color-card))', color: 'var(--color-ink)',
};
const menuStyle: React.CSSProperties = {
  position: 'absolute', zIndex: 30, top: '100%', left: 0, right: 0, marginTop: 2, padding: 'var(--sp-1)', listStyle: 'none',
  background: 'var(--color-card)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', boxShadow: 'var(--shadow-md, 0 6px 18px rgba(0,0,0,0.18))', maxHeight: 220, overflowY: 'auto',
};
const menuItemStyle: React.CSSProperties = { display: 'flex', gap: 'var(--sp-2)', alignItems: 'baseline', padding: 'var(--sp-2) var(--sp-3)', borderRadius: 6, cursor: 'pointer', fontSize: 'var(--fs-sm)' };
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 'var(--fs-xs)', color: 'var(--brand-primary)' };
